import React, { useState } from 'react';
import { X, Plus, Trash2, Check, Filter, Calendar } from 'lucide-react';
import { FilterRule } from '../types';

interface AdvancedFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  rules: FilterRule[];
  onApplyRules: (rules: FilterRule[]) => void;
  fieldOptions?: { id: string; label: string }[];
}

const DEFAULT_FIELD_OPTIONS = [
  { id: 'name', label: 'Lead Name / Account' },
  { id: 'company', label: 'Company Name (consignee)' },
  { id: 'contactPerson', label: 'Primary Contact Person' },
  { id: 'phone', label: 'Phone Number' },
  { id: 'email', label: 'Email Address' },
  { id: 'owner', label: 'Salesperson Ownership' },
  { id: 'serviceTypes', label: 'Service Types' },
  { id: 'industries', label: 'Industries' },
  { id: 'outboundStates', label: 'Outbound States' },
  { id: 'outboundWarehouses', label: 'Outbound Warehouses' },
  { id: 'daysOfService', label: 'Days of Service' },
  { id: 'fullQuoteView', label: 'Full Quote View' },
  { id: 'estimatedDealValue', label: 'Estimated Deal Value ($)' },
  { id: 'lastContacted', label: 'Last Update Date' },
  { id: 'nextSteps', label: 'Next Steps / Updates' },
  { id: 'status', label: 'Status' },
  { id: 'followUpDate', label: 'Follow-up Date' },
];

const OPERATOR_OPTIONS = [
  { id: 'contains', label: 'Contains' },
  { id: 'equals', label: 'Equals' },
  { id: 'not_equals', label: 'Does Not Equal' },
  { id: 'does_not_contain', label: 'Does Not Contain' },
  { id: 'greater_than', label: 'Is Greater Than (>)' },
  { id: 'less_than', label: 'Is Less Than (<)' },
  { id: 'is_empty', label: 'Is Empty' },
  { id: 'is_not_empty', label: 'Is Not Empty' },
  { id: 'relative_date', label: 'Relative Date Range' },
];

const RELATIVE_DATE_PRESETS = [
  'Today',
  'Yesterday',
  'This Week',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
  'This Quarter',
  'Next 30 Days',
];

export default function AdvancedFilterModal({
  isOpen,
  onClose,
  rules,
  onApplyRules,
  fieldOptions,
}: AdvancedFilterModalProps) {
  const fields = fieldOptions && fieldOptions.length > 0 ? fieldOptions : DEFAULT_FIELD_OPTIONS;
  const [localRules, setLocalRules] = useState<FilterRule[]>(rules);

  if (!isOpen) return null;

  const addRule = () => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      field: 'company',
      operator: 'contains',
      value: '',
    };
    setLocalRules([...localRules, newRule]);
  };

  const removeRule = (id: string) => {
    setLocalRules(localRules.filter((r) => r.id !== id));
  };

  const updateRule = (id: string, key: keyof FilterRule, val: any) => {
    setLocalRules(
      localRules.map((r) => (r.id === id ? { ...r, [key]: val } : r))
    );
  };

  const handleClearAll = () => {
    setLocalRules([]);
  };

  const handleApply = () => {
    onApplyRules(localRules);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900/80">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">
              Advanced Multi-Rule Criteria Builder
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex-1 overflow-y-auto max-h-[60vh] space-y-3">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            Combine multiple field conditions using custom operators and relative date presets to filter pipeline leads.
          </p>

          {localRules.length === 0 ? (
            <div className="p-6 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/40">
              <p className="text-xs text-slate-500 font-medium">
                No active filter rules. Click "Add Criteria Rule" below to start.
              </p>
            </div>
          ) : (
            localRules.map((rule, idx) => (
              <div
                key={rule.id}
                className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60"
              >
                <span className="text-[10px] font-extrabold text-blue-600 dark:text-blue-400 uppercase tracking-widest min-w-[32px]">
                  {idx === 0 ? 'WHERE' : 'AND'}
                </span>

                {/* Field Selection */}
                <select
                  value={rule.field}
                  onChange={(e) => updateRule(rule.id, 'field', e.target.value)}
                  className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                >
                  {fields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>

                {/* Operator Selection */}
                <select
                  value={rule.operator}
                  onChange={(e) => updateRule(rule.id, 'operator', e.target.value)}
                  className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                >
                  {OPERATOR_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>

                {/* Value Input or Relative Date Selector */}
                {rule.operator === 'relative_date' ? (
                  <select
                    value={rule.value}
                    onChange={(e) => updateRule(rule.id, 'value', e.target.value)}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950 text-blue-950 dark:text-blue-100 flex-1"
                  >
                    <option value="">-- Select Date Preset --</option>
                    {RELATIVE_DATE_PRESETS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                ) : rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty' ? (
                  <input
                    type="text"
                    value={rule.value}
                    onChange={(e) => updateRule(rule.id, 'value', e.target.value)}
                    placeholder="Compare value..."
                    className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 flex-1 min-w-[140px]"
                  />
                ) : null}

                <button
                  type="button"
                  onClick={() => removeRule(rule.id)}
                  className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded transition cursor-pointer"
                  title="Remove Rule"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}

          <button
            type="button"
            onClick={addRule}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 transition cursor-pointer border border-blue-200 dark:border-blue-900"
          >
            <Plus className="w-3.5 h-3.5" /> Add Criteria Rule
          </button>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 flex items-center justify-between">
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 transition cursor-pointer"
          >
            Clear All
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition cursor-pointer"
            >
              <Check className="w-3.5 h-3.5" /> Apply Filter Logic
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
